import React, { useEffect, useState } from 'react';
import BaseModal from '../BaseModal';
import { INPUT, LABEL } from '../ui';
import { addMonths, currentMonthKey, formatMoney, formatMonthLong, parseDollars } from './format';

const monthOptions = (...keep) => {
  const now = currentMonthKey();
  const keys = new Set(Array.from({ length: 37 }, (_, i) => addMonths(now, i - 24)));
  keep.filter(Boolean).forEach((key) => keys.add(key));
  return [...keys].sort();
};

const planPrice = (plan) =>
  `${formatMoney(plan.seat_cents)} / seat${plan.platform_cents ? ` + ${formatMoney(plan.platform_cents)} platform` : ''}`;

// Puts a company on a plan, or changes the one it's on. Starting in a future
// month makes the months before it a free trial; an end month stops billing
// after it. The new terms price every month not yet invoiced, this one included.
const PlanModal = ({ company, plans, onConfirm, onCancel }) => {
  const [plan, setPlan] = useState('starter');
  const [startsOn, setStartsOn] = useState(currentMonthKey());
  const [endsOn, setEndsOn] = useState('');
  const [discount, setDiscount] = useState('0');
  const [seatPrice, setSeatPrice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!company) return;
    const sub = company.subscription;
    setPlan(sub?.plan || 'starter');
    setStartsOn(sub?.starts_on || currentMonthKey());
    setEndsOn(sub?.ends_on || '');
    setDiscount(String(sub?.discount_percent ?? 0));
    setSeatPrice(sub?.seat_cents != null ? (sub.seat_cents / 100).toFixed(2) : '');
    setError('');
    setBusy(false);
  }, [company]);

  if (!company) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const percent = Number(discount);
    if (!Number.isInteger(percent) || percent < 0 || percent > 100) {
      setError('The discount is a whole percentage from 0 to 100.');
      return;
    }
    let seatCents = null;
    if (seatPrice.trim()) {
      seatCents = parseDollars(seatPrice);
      if (seatCents === null) {
        setError('Enter the seat price in dollars, like 39.00, or leave it blank for the list price.');
        return;
      }
    }
    if (endsOn && endsOn < startsOn) {
      setError('Billing has to end after it starts.');
      return;
    }
    setBusy(true);
    const message = await onConfirm({
      plan,
      starts_on: startsOn,
      ends_on: endsOn || null,
      discount_percent: percent,
      seat_cents: seatCents,
    });
    if (message) {
      setError(message);
      setBusy(false);
    }
  };

  const selected = plans.find((p) => p.key === plan);
  const months = monthOptions(startsOn, endsOn);

  return (
    <BaseModal
      open
      titleId="plan-modal-title"
      title={company.subscription ? `Change ${company.name}'s plan` : `Put ${company.name} on a plan`}
      message="Months not yet invoiced are billed on these terms, this month included. Issued invoices don't change."
      onSubmit={handleSubmit}
      onCancel={onCancel}
      confirmLabel={busy ? 'Saving…' : 'Save plan'}
      widthClass="max-w-xl"
    >
      <div className="space-y-5">
        <fieldset>
          <legend className={LABEL}>Plan</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {plans.map((p) => (
              <label
                key={p.key}
                className={`block rounded-lg border px-3 py-2.5 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-purple-400 ${
                  plan === p.key ? 'border-purple-400 bg-purple-500/15' : 'border-gray-600 hover:border-gray-500 bg-gray-700/30'
                }`}
              >
                <input
                  type="radio"
                  name="plan"
                  value={p.key}
                  checked={plan === p.key}
                  onChange={() => setPlan(p.key)}
                  className="sr-only"
                />
                <span className="block text-sm font-semibold text-white">{p.name}</span>
                <span className="block text-xs text-gray-400 mt-0.5">{planPrice(p)}</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {p.max_seats ? `Up to ${p.max_seats} seats` : 'No seat limit'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="plan-starts" className={LABEL}>Billing starts</label>
            <select id="plan-starts" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} className={INPUT}>
              {months.map((key) => (
                <option key={key} value={key}>{formatMonthLong(key)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="plan-ends" className={LABEL}>Billing ends after</label>
            <select id="plan-ends" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} className={INPUT}>
              <option value="">No end date</option>
              {months.filter((key) => key >= startsOn).map((key) => (
                <option key={key} value={key}>{formatMonthLong(key)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="plan-discount" className={LABEL}>Discount (%)</label>
            <input
              id="plan-discount"
              type="number"
              min="0"
              max="100"
              step="1"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="plan-seat-price" className={LABEL}>Negotiated seat price</label>
            <input
              id="plan-seat-price"
              inputMode="decimal"
              value={seatPrice}
              onChange={(e) => setSeatPrice(e.target.value)}
              placeholder={selected ? `List: ${formatMoney(selected.seat_cents)}` : ''}
              className={INPUT}
              autoComplete="off"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
      </div>
    </BaseModal>
  );
};

export default PlanModal;
